import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface BrainActivitySummary {
  queriesToday: number;
  queriesDeltaPct: number;
  avgResponseMs: number;
  avgResponseDeltaMs: number;
  activeConnectors: string[];
  avgToolsPerQuery: number;
}

export interface BrainActivitySummaryResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: BrainActivitySummary;
}

export interface ConnectedToolsKnowledgeGraph {
  totalNodes: number;
  totalEdges: number;
  totalDocuments: number;
}

export interface SqlDatabaseTable {
  name: string;
  count: number;
}

export interface VectorDbCollection {
  name: string;
  objects: number;
}

export interface ConnectedTools {
  knowledgeGraph: ConnectedToolsKnowledgeGraph;
  sqlDatabase: SqlDatabaseTable[];
  vectorDb: VectorDbCollection[];
}

export interface ConnectedToolsResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: ConnectedTools;
}

export interface ConnectorBreakdown {
  chatbot: number;
  inference: number;
  api: number;
}

export interface FrequentQuery {
  queryPattern: string;
  count: number;
  connectorBreakdown: ConnectorBreakdown;
  lastSeen: string;
}

export interface FrequentQueriesResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: FrequentQuery[];
}

// Dummy data for the KPI row until the backend exposes a real summary endpoint.
// Swap getSummary() for a real this.http.get(...) call once that's ready.
const DUMMY_SUMMARY: BrainActivitySummary = {
  queriesToday: 1042,
  queriesDeltaPct: 14,
  avgResponseMs: 1300,
  avgResponseDeltaMs: -180,
  activeConnectors: ['Chatbot', 'Inference', 'API', 'Classifier'],
  avgToolsPerQuery: 1.8
};

// Dummy data for the Most Frequent panel until the backend exposes a real
// per-connector breakdown endpoint. Swap getFrequentQueries() for a real
// this.http.get(...) call once that's ready — the shape below is the agreed contract.
const DUMMY_FREQUENT_QUERIES: FrequentQuery[] = [
  { queryPattern: "What's the status of order #…", count: 84, connectorBreakdown: { chatbot: 52, inference: 20, api: 12 }, lastSeen: '3 min ago' },
  { queryPattern: 'Summarize document …', count: 61, connectorBreakdown: { chatbot: 40, inference: 0, api: 21 }, lastSeen: '9 min ago' },
  { queryPattern: 'Classify uploaded batch', count: 47, connectorBreakdown: { chatbot: 0, inference: 47, api: 0 }, lastSeen: '15 min ago' },
  { queryPattern: 'Lookup customer by email', count: 38, connectorBreakdown: { chatbot: 8, inference: 0, api: 30 }, lastSeen: '22 min ago' },
  { queryPattern: 'Q3 sales by region', count: 29, connectorBreakdown: { chatbot: 29, inference: 0, api: 0 }, lastSeen: '2 min ago' }
];

@Injectable({
  providedIn: 'root'
})
export class BrainActivity {
  private connectedToolsUrl = `${environment.apiUrl}/brainactivity`;

  constructor(private http: HttpClient) {}

  getSummary(): Observable<BrainActivitySummaryResponse> {
    return of({
      statusCode: 200,
      success: true,
      message: 'Dummy data — backend endpoint pending',
      data: DUMMY_SUMMARY
    });
  }

  getConnectedTools(): Observable<ConnectedToolsResponse> {
    return this.http.get<ConnectedToolsResponse>(`${this.connectedToolsUrl}/connectedtools`);
  }

  getFrequentQueries(window: '24h' | '7d' = '24h', limit = 10): Observable<FrequentQueriesResponse> {
    return of({
      statusCode: 200,
      success: true,
      message: 'Dummy data — backend endpoint pending',
      data: DUMMY_FREQUENT_QUERIES.slice(0, limit)
    });
  }
}
